import type { FC, ReactElement } from 'react';

import useAsync from 'react-use/esm/useAsync';
import { tss } from 'tss-react/mui';

const useStyles = tss.create(({ theme }) => ({
  root: {
    '& > img, & > svg': {
      height: '100%',
      objectFit: 'contain',
      width: '100%',
    },
    'alignItems': 'center',
    'display': 'flex',
    'gap': theme.spacing(4),
    'height': '100%',
    'justifyContent': 'center',
    'width': '100%',
  },
}));

type Props = {
  className?: string;
  fallbackImage?: ReactElement | string;
  fallbackLabel?: string;
  image?: ReactElement | string;
  label?: string;
};

const checkImageExists = (src: string): Promise<boolean> =>
  new Promise(resolve => {
    const img = new Image();
    img.addEventListener('load', () => resolve(true));
    img.addEventListener('error', () => resolve(false));
    img.src = src;
  });

const getImageComponent = async (img?: ReactElement | string, alt?: string) => {
  if (!img) return null;

  if (typeof img === 'string') {
    const exists = await checkImageExists(img);
    return exists ? <img alt={alt} src={img} /> : null;
  }

  return img;
};

export const ImageWithFallback: FC<Props> = ({
  className,
  fallbackImage,
  fallbackLabel,
  image,
  label,
}) => {
  const { classes, cx } = useStyles();

  const { value: resolvedElement } = useAsync(async () => {
    const imageComponent = await getImageComponent(image, label);

    if (imageComponent) return imageComponent;

    return getImageComponent(fallbackImage, fallbackLabel);
  }, [image, fallbackImage, label, fallbackLabel]);

  return <div className={cx(classes.root, className)}>{resolvedElement}</div>;
};
